import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '@/constants/UserContext';
import { useOptimizedVolunteers } from '@/hooks/useOptimizedQueries';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import type { OptimizedVolunteer } from '@/utils/optimizedQueries';
import { useWaitingList, type Applicant } from './useWaitingList';
import { useHourRequests, type HourRequest } from './useHourRequests';
import { useEventStats, type EventStats, type ChartPoint, type VolunteersChartPoint, type PieDataItem } from './useEventStats';
import { useEventShifts } from '@/hooks/useEventShifts';
import { useEventShiftRoster } from '@/hooks/useEventShiftRoster';
import { assignVolunteerShifts, removeVolunteerShift } from '@/utils/eventShiftsApi';

/** Cap on an organisation's event list. Truncation is logged rather than silent. */
const ORG_EVENTS_LIMIT = 200;

export type VolunteersEvent = {
  id: string;
  name: string;
  is_active: boolean;
  image: string | null;
  location: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  slots: number;
  volunteerCount?: number;
  departments: string[] | null;
  time_intervals: string[] | null;
  is_public: boolean;
  created_by: string;
  created_at: string;
};

export type VolunteersTab = 'volunteers' | 'waiting' | 'shifts' | 'stats';

export interface VolunteersStats {
  totalVolunteers: number;
  totalEvents: number;
}

export type { Applicant, HourRequest, EventStats, ChartPoint, VolunteersChartPoint, PieDataItem };

export interface UseVolunteersPageOptions {
  onHourRequestError?: (message: string) => void;
  onHourRequestSuccess?: () => void;
  onWaitingListError?: (message: string) => void;
  onWaitingListWarning?: (message: string) => void;
  onShiftError?: (message: string) => void;
  /** Override the org ID — used when a volunteer is acting as an admin of an org. */
  overrideOrgId?: string | null;
}

const VOLUNTEERS_SELECTED_EVENT_KEY = 'volyx_volunteers_selected_event_id';

export function useVolunteersPage(options?: UseVolunteersPageOptions) {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const organizationId =
    options?.overrideOrgId ??
    (user?.role === 'organization' ? user.id : undefined);

  // All events for this org (active + ended) for dropdown
  const [events, setEvents] = useState<VolunteersEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const fetchAllEvents = useCallback(async () => {
    if (!organizationId) {
      setEvents([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, is_active, image, location, start_date, end_date, status, slots, departments, time_intervals, is_public, created_by, created_at')
        .eq('created_by', organizationId)
        // Sorted server-side in the same order the list is displayed, so if the cap
        // is ever hit the rows dropped are the least relevant (inactive, oldest)
        // rather than an arbitrary slice.
        .order('is_active', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(ORG_EVENTS_LIMIT);
      if (error) {
        setEvents([]);
        return;
      }
      if ((data?.length ?? 0) === ORG_EVENTS_LIMIT) {
        logger.warn(
          `useVolunteersPage: org ${organizationId} hit the ${ORG_EVENTS_LIMIT}-event cap; older events are not listed`
        );
      }
      const mapped = (data || []).map((e) => ({
        ...e,
        departments: e.departments || null,
        time_intervals: e.time_intervals || null,
      })) as VolunteersEvent[];
      mapped.sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setEvents(mapped);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  const [selectedEventId, setSelectedEventIdState] = useState<string | null>(null);
  const [manageVolunteer, setManageVolunteer] = useState<OptimizedVolunteer | null>(null);

  const volunteersUrlWithEvent = useCallback(
    (eventId: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (eventId) params.set('eventId', eventId);
      else params.delete('eventId');
      const q = params.toString();
      return q ? `/volunteers?${q}` : '/volunteers';
    },
    [searchParams],
  );

  // Sync in-memory selection from URL / storage when events load or URL changes externally.
  // Do NOT depend on selectedEventId — that caused a race where a lagging ?eventId= reverted
  // the user's freshly chosen event before navigate() updated searchParams.
  useEffect(() => {
    if (events.length === 0) return;
    const fromUrl = searchParams.get('eventId');
    const fromStorage = (() => {
      try {
        return localStorage.getItem(VOLUNTEERS_SELECTED_EVENT_KEY);
      } catch {
        return null;
      }
    })();
    const validFromUrl = fromUrl && events.some((e) => e.id === fromUrl) ? fromUrl : null;
    const validFromStorage = fromStorage && events.some((e) => e.id === fromStorage) ? fromStorage : null;

    setSelectedEventIdState((current) => {
      if (validFromUrl) {
        if (current === validFromUrl) return current;
        try {
          localStorage.setItem(VOLUNTEERS_SELECTED_EVENT_KEY, validFromUrl);
        } catch {
          // Storage unavailable (private mode); the selection still works in memory.
        }
        return validFromUrl;
      }
      const currentValid = current && events.some((e) => e.id === current);
      if (currentValid) return current;
      const next = validFromStorage || events[0].id;
      try {
        if (next) localStorage.setItem(VOLUNTEERS_SELECTED_EVENT_KEY, next);
      } catch {
        // Storage unavailable (private mode); the selection still works in memory.
      }
      return next;
    });
  }, [events, searchParams]);

  // Keep ?eventId= in the URL aligned with the in-memory selection (user picks, defaults).
  useEffect(() => {
    if (events.length === 0 || !selectedEventId) return;
    if (searchParams.get('eventId') === selectedEventId) return;
    navigate(volunteersUrlWithEvent(selectedEventId), { replace: true });
  }, [events, searchParams, selectedEventId, navigate, volunteersUrlWithEvent]);

  const setSelectedEventId = useCallback(
    (id: string | null) => {
      setSelectedEventIdState(id);
      setManageVolunteer(null);
      try {
        if (id) localStorage.setItem(VOLUNTEERS_SELECTED_EVENT_KEY, id);
        else localStorage.removeItem(VOLUNTEERS_SELECTED_EVENT_KEY);
      } catch {
        // Storage unavailable (private mode); the selection still works in memory.
      }
      navigate(volunteersUrlWithEvent(id), { replace: true });
    },
    [navigate, volunteersUrlWithEvent],
  );

  // Close manage modal when event changes via URL/deep link (not only via setSelectedEventId).
  useEffect(() => {
    setManageVolunteer(null);
  }, [selectedEventId]);

  const {
    volunteers: rawVolunteers,
    profilesUnavailable,
    loading,
    error,
    refetch,
    updateVolunteerDepartment,
  } = useOptimizedVolunteers(selectedEventId ?? undefined, organizationId);

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<VolunteersTab>('volunteers');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'waiting' || tabParam === 'volunteers' || tabParam === 'shifts' || tabParam === 'stats') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const waitingListOptions = useMemo(
    () => ({ onAcceptError: options?.onWaitingListError, onAcceptWarning: options?.onWaitingListWarning }),
    [options?.onWaitingListError, options?.onWaitingListWarning],
  );
  const waitingList = useWaitingList(selectedEventId, user?.id, activeTab, refetch, waitingListOptions);
  const hourRequestsState = useHourRequests(selectedEventId, user?.id, refetch, {
    onError: options?.onHourRequestError,
    onSuccess: options?.onHourRequestSuccess,
  });
  const eventStatsState = useEventStats(selectedEventId, activeTab);

  // --- Shifts ---
  // Only loaded on the Shifts tab: both reads are per-event and nothing else on the page
  // shows them.
  const shiftsEnabled = activeTab === 'shifts' && Boolean(selectedEventId);
  const { shifts, loading: shiftsLoading, refetch: refetchShifts } = useEventShifts(selectedEventId, shiftsEnabled);
  const { roster: shiftRoster, refetch: refetchShiftRoster } = useEventShiftRoster(selectedEventId, shiftsEnabled);
  const onShiftError = options?.onShiftError;

  const refreshShifts = useCallback(async () => {
    await Promise.all([refetchShifts(), refetchShiftRoster()]);
  }, [refetchShifts, refetchShiftRoster]);

  const assignVolunteerToShift = useCallback(
    async (shiftId: string, volunteerUserId: string) => {
      if (!selectedEventId) return;
      // Capacity is enforced in the database, so a shift that filled up while this list was
      // open comes back as full rather than being overfilled.
      const result = await assignVolunteerShifts(selectedEventId, volunteerUserId, [shiftId]);
      if (!result.ok) onShiftError?.(result.error);
      else if (result.full.length > 0) onShiftError?.('This shift is full.');
      await refreshShifts();
    },
    [selectedEventId, onShiftError, refreshShifts],
  );

  const removeVolunteerFromShift = useCallback(
    async (shiftId: string, volunteerUserId: string) => {
      const result = await removeVolunteerShift(shiftId, volunteerUserId);
      if (!result.ok) onShiftError?.(result.error);
      await refreshShifts();
    },
    [onShiftError, refreshShifts],
  );

  const normalizedSearch = search.trim().toLowerCase();

  const volunteers = useMemo(
    () =>
      rawVolunteers.filter((volunteer) => {
        if (!normalizedSearch) return true;
        const profile = volunteer.profiles;
        const eventName = volunteer.events?.name ?? '';
        const fullName = profile
          ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
          : '';
        const email = profile?.email ?? '';
        return [fullName, email, eventName].join(' ').toLowerCase().includes(normalizedSearch);
      }),
    [rawVolunteers, normalizedSearch],
  );

  const stats: VolunteersStats = useMemo(() => {
    const uniqueVolunteerIds = new Set(rawVolunteers.map((v) => v.volunteer_id || v.id));
    const uniqueEventIds = new Set(rawVolunteers.map((v) => v.event_id));
    return { totalVolunteers: uniqueVolunteerIds.size, totalEvents: uniqueEventIds.size };
  }, [rawVolunteers]);

  // --- Tasks ---
  const [tasksCount, setTasksCount] = useState(0);
  const [showTasksModal, setShowTasksModal] = useState(false);

  useEffect(() => {
    setTasksCount(0);
  }, [selectedEventId]);

  const fetchTasksCount = useCallback(async () => {
    if (!selectedEventId) { setTasksCount(0); return; }
    try {
      const { count, error: err } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', selectedEventId)
        .neq('status', 'completed');
      if (err) { setTasksCount(0); return; }
      setTasksCount(count || 0);
    } catch {
      setTasksCount(0);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (selectedEventId) fetchTasksCount();
  }, [selectedEventId, fetchTasksCount]);

  // Selected event data
  const selectedEvent = useMemo(() => events.find((e) => e.id === selectedEventId) || null, [events, selectedEventId]);

  return {
    loading: loading || eventsLoading,
    error,
    profilesUnavailable,
    refetch,
    updateVolunteerDepartment,
    events,
    eventsLoading,
    refetchEvents: fetchAllEvents,
    selectedEventId,
    setSelectedEventId,
    selectedEvent,
    volunteers,
    rawVolunteers,
    search,
    setSearch,
    activeTab,
    setActiveTab,
    stats,
    // Shifts
    shifts,
    shiftRoster,
    shiftsLoading,
    assignVolunteerToShift,
    removeVolunteerFromShift,
    // Waiting list (from useWaitingList)
    applicants: waitingList.applicants,
    loadingApplicants: waitingList.loadingApplicants,
    fetchVolunteerApplications: waitingList.fetchVolunteerApplications,
    handleAcceptApplicant: waitingList.handleAcceptApplicant,
    handleDenyApplicant: waitingList.handleDenyApplicant,
    handleAcceptAll: waitingList.handleAcceptAll,
    // Hour requests (from useHourRequests)
    hourRequests: hourRequestsState.hourRequests,
    hourRequestsCount: hourRequestsState.hourRequestsCount,
    loadingHourRequests: hourRequestsState.loadingHourRequests,
    showHourRequestsModal: hourRequestsState.showHourRequestsModal,
    setShowHourRequestsModal: hourRequestsState.setShowHourRequestsModal,
    fetchHourRequests: hourRequestsState.fetchHourRequests,
    handleAcceptHourRequest: hourRequestsState.handleAcceptHourRequest,
    handleDenyHourRequest: hourRequestsState.handleDenyHourRequest,
    // Tasks
    tasksCount,
    showTasksModal,
    setShowTasksModal,
    fetchTasksCount,
    // Stats (from useEventStats)
    eventStats: eventStatsState.eventStats,
    chartData: eventStatsState.chartData,
    volunteersChartData: eventStatsState.volunteersChartData,
    pieChartData: eventStatsState.pieChartData,
    loadingStats: eventStatsState.loadingStats,
    fetchEventStats: eventStatsState.fetchEventStats,
    manageVolunteer,
    setManageVolunteer,
  };
}
